import React, { useEffect } from "react";

interface RegistrationPreviewProps {
  data: any;
  imagePreview: string | null;
  countryCodeOptions: Array<{
    _id: string;
    value: string;
    name: string;
    flag: string;
  }>;
  mandirOptions: Array<{
    _id: string;
    title: string;
  }>;
  loading: boolean;
  onEdit: () => void;
  onSubmit: () => void;
}

export const RegistrationPreview: React.FC<RegistrationPreviewProps> = ({
  data,
  imagePreview,
  countryCodeOptions,
  mandirOptions,
  loading,
  onEdit,
  onSubmit,
}) => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const handleEdit = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    onEdit();
  };

  const formatMobileNumber = (countryCode: string, mobileNumber: string) => {
    const countryOption = countryCodeOptions.find(
      (opt) => opt.value === countryCode
    );
    if (!countryOption) return `${countryCode} ${mobileNumber}`;

    // Country-specific formatting patterns
    const patterns = {
      GB: [4, 3, 3], // UK: XXXX XXX XXX
      US_CA: {
        format: (n: string) =>
          `(${n.slice(0, 3)}) ${n.slice(3, 6)}-${n.slice(6)}`,
        length: 10,
      }, // US/CA: (XXX) XXX-XXXX
      IN: [5, 5], // India: XXXXX XXXXX
      AU: [3, 3, 3], // Australia: XXX XXX XXX
    };

    const pattern = patterns[countryOption._id as keyof typeof patterns];
    let formattedNumber = mobileNumber;

    if (pattern) {
      if (typeof pattern === "object" && "format" in pattern) {
        // Custom formatter (like US/CA)
        if (mobileNumber.length === pattern.length) {
          formattedNumber = pattern.format(mobileNumber);
        }
      } else if (Array.isArray(pattern)) {
        // Array-based chunking
        const expectedLength = pattern.reduce((sum, chunk) => sum + chunk, 0);
        if (mobileNumber.length === expectedLength) {
          let index = 0;
          formattedNumber = pattern
            .map((chunkSize) => {
              const chunk = mobileNumber.slice(index, index + chunkSize);
              index += chunkSize;
              return chunk;
            })
            .join(" ");
        }
      }
    } else if (mobileNumber.length >= 8) {
      // Default: split in half
      const mid = Math.ceil(mobileNumber.length / 2);
      formattedNumber = `${mobileNumber.slice(0, mid)} ${mobileNumber.slice(
        mid
      )}`;
    }

    return `${countryOption.flag} ${countryOption.value} ${formattedNumber}`;
  };

  const getSevaPreferences = () => {
    if (
      !data.wouldLikeToVolunteer ||
      !data.sevaDepartments ||
      data.sevaDepartments.length === 0
    ) {
      return "Not interested in volunteering";
    }
    return data.sevaDepartments.join(", ");
  };

  const getMandirTitle = (mandirId: string) => {
    const mandir = mandirOptions.find((opt) => opt._id === mandirId);
    return mandir ? mandir.title : mandirId; // Fallback to ID if not found
  };

  return (
    <div className="card mb-5 mb-xl-10">
      <div className="card-header border-0 p-4 p-lg-5 d-flex flex-column flex-sm-row align-items-center text-center text-sm-start">
        <div className="me-sm-4 mb-3 mb-sm-0" style={{ flexShrink: 0 }}>
          <img
            src="/media/logos/logo-2.svg"
            alt="Swaminarayan Gadi Logo"
            style={{
              width: "clamp(50px, 12vw, 60px)",
              height: "clamp(50px, 12vw, 60px)",
              objectFit: "contain",
            }}
          />
        </div>
        {/* Title */}
        <div className="flex-grow-1">
          <h3 className="card-title fw-bolder fs-4 fs-lg-3 mb-1">
            Maninagar Shree Swaminarayan Gadi Sansthan
          </h3>
          <span className="card-label fs-5 fs-lg-4">
            Shree Swaminarayan Gadi Parivar Registration Form
          </span>
        </div>
      </div>

      <div className="card-body border-top p-9">
        <div className="text-center text-sm-start mb-8">
          <h3 className="card-title fw-bolder fs-4 fs-lg-3 mb-2">
            Review Your Registration Details
          </h3>
          <p className="card-label fs-6 fs-lg-5 text-muted mb-0">
            Please review all information carefully before submitting
          </p>
        </div>

        {/* Contact Information Section */}
        <div className="section-header mb-6 mt-8">
          <h3 className="section-title">
            <i className="fas fa-address-card me-2"></i>Contact Information
          </h3>
        </div>

        <div className="row mb-6">
          <div className="col-lg-4 col-md-4 col-sm-12 mb-3">
            <label className="fw-bold fs-6 text-muted">Given Name</label>
            <div className="fs-5 fw-semibold">{data.firstName}</div>
          </div>
          <div className="col-lg-4 col-md-4 col-sm-12 mb-3">
            <label className="fw-bold fs-6 text-muted">
              Father/Husband Name
            </label>
            <div className="fs-5 fw-semibold">{data.middleName}</div>
          </div>
          <div className="col-lg-4 col-md-4 col-sm-12 mb-3">
            <label className="fw-bold fs-6 text-muted">Surname</label>
            <div className="fs-5 fw-semibold">{data.lastName}</div>
          </div>
        </div>

        <div className="row mb-6">
          <div className="col-lg-4 col-md-6 mb-3">
            <label className="fw-bold fs-6 text-muted">
              Mobile Number (Whatsapp)
            </label>
            <div className="fs-5 fw-semibold text-break">
              {formatMobileNumber(data.mobileCountryCode, data.mobileNumber)}
            </div>
          </div>
          <div className="col-lg-4 col-md-6 mb-3">
            <label className="fw-bold fs-6 text-muted">Email ID</label>
            <div className="fs-5 fw-semibold text-break">{data.email}</div>
          </div>
        </div>

        {/* Photo Preview - Mobile First Approach */}
        {imagePreview && (
          <div className="row mb-6">
            <div className="col-12">
              <label className="fw-bold fs-6 mb-3 d-block">
                Self-Portrait Photo
              </label>
              <div className="d-inline-block">
                <div
                  className="image-input image-input-outline shadow-sm"
                  style={{
                    width: "clamp(120px, 30vw, 150px)",
                    height: "clamp(120px, 30vw, 150px)",
                    maxWidth: "150px",
                    maxHeight: "150px",
                    borderRadius: "50%",
                  }}
                >
                  <div
                    className="image-input-wrapper w-100 h-100"
                    style={{
                      backgroundImage: `url(${imagePreview})`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      borderRadius: "50%",
                    }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Mandir/Mandal Information Section */}
        <div className="section-header mb-6 mt-8">
          <h3 className="section-title">
            <i className="fas fa-hand-holding-heart me-2"></i>
            Mandir/Mandal Information
          </h3>
        </div>

        <div className="row mb-8">
          <div className="col-lg-4 col-md-6 mb-3">
            <label className="fw-bold fs-6 text-muted">
              Local Mandir/Mandal
            </label>
            <div className="fs-5 fw-semibold">
              {getMandirTitle(data.localMandir)}
            </div>
          </div>
          <div className="col-lg-4 col-md-6 mb-3">
            <label className="fw-bold fs-6 text-muted">Native Village</label>
            <div className="fs-5 fw-semibold">
              {data.nativeVillage === "Other" && data.customNativeVillage
                ? data.customNativeVillage
                : data.nativeVillage}
            </div>
          </div>
        </div>

        {/* Address Information Section */}
        <div className="section-header mb-6 mt-8">
          <h3 className="section-title">
            <i className="fas fa-map-marker-alt me-2"></i>Address Information
          </h3>
        </div>

        <div className="row mb-6">
          <div className="col-lg-4 col-md-6 mb-3">
            <label className="fw-bold fs-6 text-muted">Home Address</label>
            <div className="fs-5 fw-semibold">{data.street}</div>
          </div>
          {data.area && (
            <div className="col-lg-4 col-md-6 mb-3">
              <label className="fw-bold fs-6 text-muted">Area</label>
              <div className="fs-5 fw-semibold">{data.area}</div>
            </div>
          )}
          <div className="col-lg-4 col-md-4 col-sm-12 mb-3">
            <label className="fw-bold fs-6 text-muted">City</label>
            <div className="fs-5 fw-semibold">{data.city}</div>
          </div>
        </div>

        <div className="row mb-6">
          {data.state && (
            <div className="col-lg-4 col-md-4 col-sm-12 mb-3">
              <label className="fw-bold fs-6 text-muted">State/Province</label>
              <div className="fs-5 fw-semibold">{data.state}</div>
            </div>
          )}
          <div className="col-lg-4 col-md-4 col-sm-12 mb-3">
            <label className="fw-bold fs-6 text-muted">
              Postal/PIN/ZIP Code
            </label>
            <div className="fs-5 fw-semibold">{data.postcode}</div>
          </div>
          <div className="col-lg-4 col-md-4 col-sm-12 mb-3">
            <label className="fw-bold fs-6 text-muted">Country</label>
            <div className="fs-5 fw-semibold">{data.country}</div>
          </div>
        </div>

        {/* Personal Information Section */}
        <div className="section-header mb-6 mt-8">
          <h3 className="section-title">
            <i className="fas fa-user me-2"></i>Personal Information
          </h3>
        </div>

        <div className="row mb-6">
          <div className="col-lg-3 col-md-12 col-sm-12 mb-6">
            <label className="fw-bold fs-6 text-muted">Date of Birth</label>
            <div className="fs-5 fw-semibold">
              {(() => {
                // Date is stored as YYYY-MM-DD string, parse and format for display
                const dateValue = data.dateOfBirth;

                if (!dateValue || typeof dateValue !== "string") {
                  return "Not provided";
                }

                // Parse YYYY-MM-DD string
                const [year, month, day] = dateValue
                  .split("-")
                  .map((n: string) => parseInt(n, 10));

                // Format for display
                const monthNames = [
                  "January",
                  "February",
                  "March",
                  "April",
                  "May",
                  "June",
                  "July",
                  "August",
                  "September",
                  "October",
                  "November",
                  "December",
                ];

                return `${day} ${monthNames[month - 1]} ${year}`;
              })()}
            </div>
          </div>

          <div className="col-lg-3 col-md-6 col-sm-12 mb-6">
            <label className="fw-bold fs-6 text-muted">Gender</label>
            <div className="fs-5 fw-semibold">{data.gender}</div>
          </div>
          <div className="col-lg-3 col-md-6 col-sm-12 mb-6">
            <label className="fw-bold fs-6 text-muted">Marital Status</label>
            <div className="fs-5 fw-semibold">{data.maritalStatus}</div>
          </div>
          <div className="col-lg-3 col-md-6 col-sm-12 mb-6">
            <label className="fw-bold fs-6 text-muted">Blood Group</label>
            <div className="fs-5 fw-semibold">{data.bloodGroup}</div>
          </div>
        </div>

        {/* Professional Information Section */}
        <div className="section-header mb-6 mt-12">
          <h3 className="section-title">
            <i className="fas fa-briefcase me-2"></i>Professional Information
          </h3>
        </div>

        <div className="row mb-6">
          <div className="col-lg-3 col-md-6 col-sm-12 mb-4 mb-lg-0">
            <label className="fw-bold fs-6 text-muted">Education</label>
            <div className="fs-5 fw-semibold">{data.education}</div>
          </div>
          <div className="col-lg-3 col-md-6 col-sm-12 mb-4 mb-lg-0">
            <label className="fw-bold fs-6 text-muted">Profession</label>
            <div className="fs-5 fw-semibold">{data.profession}</div>
          </div>
          <div className="col-lg-3 col-md-6 col-sm-12 mb-4 mb-lg-0">
            <label className="fw-bold fs-6 text-muted">Retirement Status</label>
            <div className="fs-5 fw-semibold">
              {data.isRetired ? "Retired" : "Not Retired"}
            </div>
          </div>
        </div>

        {/* Seva Preferences Section */}
        <div className="section-header mb-6 mt-8">
          <h3 className="section-title">
            <i className="fas fa-hands-helping me-2"></i>Seva Preferences
          </h3>
        </div>

        <div className="row mb-6">
          <div className="col-lg-12">
            <label className="fw-bold fs-6 text-muted">
              Would you like to volunteer for Seva?
            </label>
            <div className="fs-5 fw-semibold mb-3">
              {data.wouldLikeToVolunteer ? "Yes" : "No"}
            </div>
          </div>
        </div>

        {data.wouldLikeToVolunteer && (
          <div className="row mb-6">
            <div className="col-12 mb-3">
              <label className="fw-bold fs-6 text-muted">
                Seva Departments
              </label>
              <div className="fs-5 fw-semibold">{getSevaPreferences()}</div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="row pt-5">
          <div className="col-md-6 mb-3 mb-md-0">
            <button
              type="button"
              className="btn btn-secondary btn-lg w-100"
              onClick={handleEdit}
            >
              <i className="fas fa-arrow-left me-2"></i>
              <span className="d-none d-sm-inline">Edit Information</span>
              <span className="d-sm-none">Edit</span>
            </button>
          </div>
          <div className="col-md-6">
            <button
              type="button"
              className="btn btn-primary btn-lg w-100"
              onClick={onSubmit}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  <span className="d-none d-sm-inline">Submitting...</span>
                  <span className="d-sm-none">Submitting...</span>
                </>
              ) : (
                <>
                  <i className="fas fa-paper-plane me-2"></i>
                  <span className="d-none d-sm-inline">
                    {window.location.pathname.includes("edit")
                      ? "Update"
                      : "Submit for Verification"}
                  </span>
                  <span className="d-sm-none">
                    {window.location.pathname.includes("edit")
                      ? "Update"
                      : "Submit"}
                  </span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
